import Sider from "antd/lib/layout/Sider";
import {ReactComponent as Close} from "../../../assets/close.svg";
import {CnkLogo} from "../header/header";
import {Button, Divider, Modal} from "antd";
import styles from './mapSider.module.less';
import {useDispatch, useSelector} from "react-redux";
import {getGlobalLanguage} from "../../../redux/languageSlice";
import {ReactComponent as Arrow} from "../../../assets/arrow.svg";
import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import mapSiderStrings from "./mapSider.strings";
import {getCurrentUser} from "../../../redux/applicationUserSlice";
import {RoutingPaths} from "../../routing/routingContainer";
import {useAuth} from "oidc-react";
import {LogoutOutlined} from "@ant-design/icons";
import {AsyncButton} from "digimuth-components/dist";
import CustomCheckbox from "../../../components/CustomCheckbox/customCheckbox";
import {
    getContrast,
    getDevGpsDebugOverlay,
    getFacilities,
    setContrast,
    setDevGpsDebugOverlay,
    setFacilities
} from "../../../redux/settingsSlice";
import loginPageStrings from "../../loginPage/loginPage.strings";
import useCurrentUserRoles from "../../../hooks/useCurrentUserRoles";
import { ApplicationUserRole } from "../../../api";

const areTemporaryDebugFeaturesEnabled = false;

export default function MapSider(props: MapSiderProps) {
    const auth = useAuth();
    const navigate = useNavigate();
    const language = useSelector(getGlobalLanguage);
    const currentUser = useSelector(getCurrentUser);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const dispatch = useDispatch();
    const [breakpointHit, setBreakpointHit] = useState(false);
    const siderRef = useRef<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
        const target = siderRef.current;
        if (!target) return
        const disablePinchZoom = (e: any) => {
            if (e.touches.length > 1) {
                e.preventDefault()
            }
        }
        target.addEventListener("touchmove", disablePinchZoom, {passive: false})
        return () => {
            target.removeEventListener("touchmove", disablePinchZoom)
        }
    }, [])

    useEffect(() => {
        if (breakpointHit) {
            const handleClickOutside = (event: any) => {
                if (siderRef.current && !siderRef.current.contains(event.target) && !props.siderCollapsed) {
                    props.setSiderCollapsed && props.setSiderCollapsed(true);
                }
            };
            document.addEventListener('click', handleClickOutside, true);

            return () => {
                document.removeEventListener('click', handleClickOutside, true);
            };
        }
    }, [props.setSiderCollapsed, props.siderCollapsed]);

    const onNavigation = () => {
        if (breakpointHit) {
            props.setSiderCollapsed(true);
        }
    }

    const onLogoutPressed = async () => {
        await auth.signOut();
        await auth.signOutRedirect();
        navigate(RoutingPaths.homePage.route);
    }

    const onLoginPressed = async () => {
        await auth.userManager.signinSilent().catch(() =>
            auth.signIn({state: {targetPath: RoutingPaths.homePage.route}}));
    }

    const goToSettings = async () => {
        if (breakpointHit) {
            props.setSiderCollapsed(true);
        }
        navigate(RoutingPaths.changePassword.route);
    }

    const name = currentUser?.userName ?? "";
    const nameStyles = {
        fontSize: name.length > 22 ? "16px" : "20px",
        wordWrap: name.length > 30 ? "break-word" : "initial"
    } as any;

    return <div className={styles.sider}>
        <Sider
            breakpoint="lg"
            ref={siderRef}
            collapsed={props.siderCollapsed}
            trigger={null}
            width={310}
            onBreakpoint={b => {
                setBreakpointHit(b);
                props.setSiderCollapsed(b);
            }}
            collapsedWidth="0">
            <div className={styles.siderHeader}>
                <CnkLogo onClick={onNavigation}/>
                <Button type="text" icon={<Close/>} onClick={() => props.setSiderCollapsed(true)}/>
            </div>
            <Button
                type="text"
                className={styles.backToWebsiteButton}
                onClick={() => window.location.href = "https://pikniknaukowy.pl/"}
            >
                <Arrow className={styles.backToWebsiteButtonArrow}/>
                <span>{mapSiderStrings.backToWebsite[language]}</span>
            </Button>
            <NavigationMenu
                setIsModalOpen={setIsModalOpen}
                onPress={onNavigation}
                map={mapSiderStrings.map[language]}
                stands={mapSiderStrings.stands[language]}
                myList={mapSiderStrings.myList[language]}
                mapSettings={mapSiderStrings.mapSettings[language]}
            />

            <div className={styles.accountSection}>
                {auth.userData ?
                    <>
                        <div className={styles.loginLabel}>
                            {loginPageStrings.loggedInAs[language]}
                        </div>
                        <Link to={RoutingPaths.changePassword.route} className={styles.loginName} style={nameStyles}
                              onClick={goToSettings}>
                            {
                                name.length > 30
                                    ? <>
                                        <div>{name.split('@')[0]}</div>
                                        <div>@{name.split('@')[1]}</div>
                                    </>
                                    : <>
                                        <div>{name}</div>
                                    </>
                            }
                        </Link>

                        <LogoutOutlined/>
                        <AsyncButton type="text" onClick={onLogoutPressed}>
                            {loginPageStrings.logout[language]}
                        </AsyncButton>
                    </>
                    :
                    <div className={styles.loginActionWrapper}>
                        <AsyncButton className={styles.secondaryButton} onClick={onLoginPressed}>
                            {loginPageStrings.text[language]}
                        </AsyncButton>
                    </div>
                }
            </div>
            <MapSettingsModal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} onNavigation={onNavigation} />
        </Sider>
    </div>
}

export function MapSettingsModal({ isModalOpen, setIsModalOpen, onNavigation }: { isModalOpen: boolean, setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>, onNavigation?: () => void }) {
    const isDevelopment = process.env.NODE_ENV === "development";
    const canShowDebugSettings = isDevelopment && areTemporaryDebugFeaturesEnabled;
    const language = useSelector(getGlobalLanguage);

    const highContrastValue = useSelector(getContrast);
    const facilitiesValue = useSelector(getFacilities);
    const devGpsDebugOverlayValue = useSelector(getDevGpsDebugOverlay);
    const [localContrast, setLocalContrast] = useState(highContrastValue);
    const [localFacilities, setLocalFacilities] = useState(facilitiesValue);
    const [localDevGpsDebugOverlay, setLocalDevGpsDebugOverlay] = useState(devGpsDebugOverlayValue);
    const dispatch = useDispatch();

    const saveChanges = () => {
        dispatch(setContrast(localContrast));
        dispatch(setFacilities(localFacilities));
        if (canShowDebugSettings) {
            dispatch(setDevGpsDebugOverlay(localDevGpsDebugOverlay));
        }
    }

    const resetChanges = () => {
        setLocalContrast(highContrastValue);
        setLocalFacilities(facilitiesValue);
        setLocalDevGpsDebugOverlay(devGpsDebugOverlayValue);
    };

    return <Modal
        title={mapSiderStrings.mapSettings[language]}
        open={isModalOpen}
        onOk={() => {
            setIsModalOpen(false);
            saveChanges();
            onNavigation?.();
        } }
        onCancel={() => {
            setIsModalOpen(false);
            resetChanges();
            onNavigation?.();
        } }
        cancelText={mapSiderStrings.cancel[language]}
        okText={"Ok"}
    >
        <div style={{ display: "flex", flexDirection: "column", gap: "15px", marginTop: 20 }}>
            <CustomCheckbox wrapperClassName={styles.checkboxWrapper}
                text={mapSiderStrings.highContrast[language]}
                defaultValue={highContrastValue} onChange={setLocalContrast}
                textStyles={styles.checkboxText} value={localContrast} />
            <div className={styles.settingsSection}>
                <div className={styles.settingsSectionTitle}>
                    {mapSiderStrings.navigationSettings[language]}
                </div>
            </div>
            <CustomCheckbox wrapperClassName={styles.checkboxWrapper}
                text={mapSiderStrings.facilities[language]} defaultValue={facilitiesValue}
                onChange={setLocalFacilities} textStyles={styles.checkboxText}
                value={localFacilities} />
            {canShowDebugSettings && <>
                <CustomCheckbox wrapperClassName={styles.checkboxWrapper}
                    text={"Debug GPS overlay"}
                    defaultValue={devGpsDebugOverlayValue}
                    onChange={setLocalDevGpsDebugOverlay}
                    textStyles={styles.checkboxText}
                    value={localDevGpsDebugOverlay} />
            </>}
        </div>
    </Modal>;
}

function NavigationMenu(props: {
    map: string,
    stands: string,
    myList: string,
    mapSettings: string,
    onPress: () => any,
    setIsModalOpen: any
}) {
    const userRoles = useCurrentUserRoles();

    return <div className={styles.navigationMenu}>
        <Link to="/" onClick={() => props.onPress()}>
            <Button type="text">
                {props.map}
            </Button>
        </Link>
        <Link to="/stands" onClick={() => props.onPress()}>
            <Button type="text">
                {props.stands}
            </Button>
        </Link>
        <Link to="/favourites" onClick={() => props.onPress()}>
            <Button type="text">
                {props.myList}
            </Button>
        </Link>
        <Button type="text" onClick={() => {
            props.setIsModalOpen(true);
        }}>
            {props.mapSettings}
        </Button>
        
        {userRoles?.includes(ApplicationUserRole.Admin) &&
        <>
        <Divider />
        <Link to="/cms" onClick={() => props.onPress()}>
            <Button type="text">
                Panel zarządzania
            </Button>
        </Link>
        </>
        }
    </div>
}

interface MapSiderProps {
    siderCollapsed: boolean,
    setSiderCollapsed: (v: boolean) => void
}
